import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useSearchParams,
} from 'react-router';
import {
  get,
  isEmpty,
} from 'lodash';
import {
  LocationHelper,
  MimeTypeHelper,
  Field2 as Field,
  ProgressBar,
  TimeUtil,
  VibeIcon,
  viLoopVideo,
  viUploadVisual,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import './VisualUpload.scss';

function VisualUpload({
  messageStatus,
  file,
  targetScreen,
  fileIndex,
  onUpdateVisualFiles,
  onUpdate = () => {},
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = searchParams.get('type') === 'new';
  const isCopy = searchParams.get('type') === 'copy';
  const isReRequest = searchParams.get('type') === 're-request';

  const getFileSize = (fileSize) => {
    // if less than a kb, add leading zero
    // if more that mb (1000000), display in mbs
    if (fileSize > 1000000) {
      return `${(fileSize / 1000000).toFixed(3)} MB`;
    }
    return `${Math.round(fileSize / 1024)} KB`;
  };

  const [uploadProgress, setUploadProgress] = useState(0);
  const [fadeOutProgress, setFadeOutProgress] = useState(false);

  const uploadItem = !isEmpty(file) ? {
    file: file.file,
    data: file.file.path,
    width: file.isNew ? file.actualDimensions.width : file.file.width,
    height: file.isNew ? file.actualDimensions.height : file.file.height,
    size: getFileSize(file.file.size),
    durationSeconds: file.isNew ? file.durationSeconds : file.file.durationSeconds,
  } : {
    file: {},
    data: '',
    width: 0,
    height: 0,
    size: 0,
    durationSeconds: 0,
  }

  // const [uploadItem, setUploadItem] = useState(!isEmpty(file) ? {
  //   file: file.file,
  //   data: file.file.path,
  //   width: file.isNew ? file.actualDimensions.width : file.file.width,
  //   height: file.isNew ? file.actualDimensions.height : file.file.height,
  //   size: getFileSize(file.file.size),
  //   durationSeconds: file.isNew ? file.durationSeconds : file.file.durationSeconds,
  // } : {
  //   file: {},
  //   data: '',
  //   width: 0,
  //   height: 0,
  //   size: 0,
  //   durationSeconds: 0,
  // });

  // TODO: Uncomment to have upload not update when state changes.
  // Possible issue with setting the file on the parent component.
  // Need to rewrite to support normal onUpdate pattern.
  
  // useEffect(() => {
  //   // console.log('file', file);
  //   setUploadItem(!isEmpty(file) ? {
  //     file: file.file,
  //     data: file.file.path,
  //     width: file.isNew ? file.actualDimensions.width : file.file.width,
  //     height: file.isNew ? file.actualDimensions.height : file.file.height,
  //     size: getFileSize(file.file.size),
  //     durationSeconds: file.isNew ? file.durationSeconds : file.file.durationSeconds,
  //   } : {
  //     file: {},
  //     data: '',
  //     width: 0,
  //     height: 0,
  //     size: 0,
  //     durationSeconds: 0,
  //   });
  // }, [file, file.name]);

  const onProgress = (data) => {
    const uploadData = data || 100;
    setUploadProgress(Math.round(uploadData));
    if (data === 100) {
      setTimeout(() => {
        setFadeOutProgress(true);
        setTimeout(() => {
          setUploadProgress(0);
          setFadeOutProgress(false);
        }, 3000);
      }, 3000);
    }
  };

  const onUploadedItemClick = (e) => {
    if (isNew || isCopy || isReRequest || (messageStatus !== 'final-cut-uploaded' && messageStatus !== 'completed')) {
      setUploadProgress(0);
    } else {
      // do not allow upload
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onDrop = (fileObj) => {
    const file = get(fileObj, '[0]', {
      file: null,
    });

    if (file && file.error) {
      console.warn('File has an error', fileObj);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const {
        target: {
          result,
        },
      } = e;

      let isError = false;
      if (file.file.type.search('image') !== -1) {
        const image = new Image();
        image.onload = () => {
          // Save file data to state to show as a preview
          // setUploadItem({
          //   file: file.file,
          //   data: result,
          //   width: image.naturalWidth,
          //   height: image.naturalHeight,
          //   size: getFileSize(file.file.size),
          //   isNew: true,
          // });
          // eslint-disable-next-line max-len
          onUpdateVisualFiles(file.file, fileIndex, { width: image.naturalWidth, height: image.naturalHeight }, 0, targetScreen, false, true, onProgress);
        };
        image.src = result;
      } else {
        const videoElement = document.createElement('VIDEO');
        videoElement.src = result;
        videoElement.addEventListener('loadedmetadata', () => {
          isError = !(LocationHelper.getDisplayAspectRatio([videoElement.videoWidth, videoElement.videoHeight])
            === LocationHelper.getDisplayAspectRatio(targetScreen.size));

          // setUploadItem({
          //   file: file.file,
          //   data: result,
          //   width: videoElement.videoWidth,
          //   height: videoElement.videoHeight,
          //   size: getFileSize(file.file.size),
          //   durationSeconds: videoElement.duration,
          //   isNew: true,
          // });
          // eslint-disable-next-line max-len
          onUpdateVisualFiles(file.file, fileIndex, { width: videoElement.videoWidth, height: videoElement.videoHeight }, videoElement.duration, targetScreen, isError, true, onProgress);
        });
      }

      onUpdate({
        file: file.file,
      });
    };

    reader.onabort = () => console.error('file reading was aborted');
    reader.onerror = () => console.error('file reading has failed');

    reader.readAsDataURL(file.file);
  };

  const getMinAndSeconds = () => {
    const durationSeconds = uploadItem.durationSeconds ? uploadItem.durationSeconds : uploadItem.file.durationSeconds;
    const loopItemMinutesSeconds = TimeUtil.convertSeconds(durationSeconds).split(':');
    let minSecDiv = null;
    if (loopItemMinutesSeconds[0] > 0) {
      minSecDiv = <span>{parseInt(loopItemMinutesSeconds[0], 10)} min</span>;
    }
    const finalDiv = minSecDiv ? <span> {minSecDiv} <span>{loopItemMinutesSeconds[1]} sec</span></span>
      : <span> <span>{parseInt(loopItemMinutesSeconds[1], 10)} sec</span></span>;
    return finalDiv;
  };

  const hasUploadFile = !isEmpty(uploadItem.file);
  const disabled = false;
  let placeholderHtml;

  if (hasUploadFile) {
    placeholderHtml = (
      <div
        className="uploaded-container"
        onClick={onUploadedItemClick}
      >
        <div className="uploaded-content">
          <div className="upload-data">
            <div className="upload-name">
              <VibeIcon
                icon={viLoopVideo}
                color={color.primary}
                size={20}
              />
              <span className="filename">{get(uploadItem, 'file.name', 'Unknown')}</span>
            </div>
            <div className="uploaded-stats">
              {get(uploadItem, 'size', '0')}, {get(uploadItem, 'width', '0')}x{get(uploadItem, 'height', '0')},
              {getMinAndSeconds()}
            </div>
            { (uploadProgress > 0 && !file.isError) && (
              <>
                <ProgressBar
                  className={classNames('upload-progress', { fadeOut: fadeOutProgress })}
                  progress={uploadProgress}
                  color={color.success}
                  height={4}
                />
                <div
                  className={classNames('progress-bar-label', { fadeOut: fadeOutProgress })}
                >
                  {uploadProgress}%
                </div>
              </>
            )}
          </div>
        </div>
        {(isNew || isCopy || isReRequest || (messageStatus !== 'final-cut-uploaded' && messageStatus !== 'completed'))
          && (
            <div
              className="upload-replace"
            >
              Replace
            </div>
          )}
      </div>
    );
  } else {
    placeholderHtml = (
      <div className="upload-container">
        <div className="upload-content">
          <div className="upload-icon">
            <VibeIcon
              icon={viUploadVisual}
              color={color.primary}
              size={20}
            />
          </div>
          <span className="drag-and-drop-text">Drag & Drop</span>
          <span className="click-to-upload-text">or click to upload</span>
        </div>
      </div>
    );
  }

  return (
    <div className="VisualUpload">
      <Grid spacing={2} container>
        <Grid size={12}>
          <Field
            type="dropzone"
            dropzoneProps={{
              type: 'document',
              style: {
                height: 94,
                minHeight: 'auto',
              },
              placeholder: (
                <div className={classNames('upload-main', { disabled }, { error: file.isError })}>
                  {placeholderHtml}
                </div>
              ),
              allowUpload: true,
              mimeTypes: MimeTypeHelper.display,
            }}
            onDrop={onDrop}
          />
          {file.isError && (
            <div className="resolution-error">
              The uploaded file&apos;s aspect ratio must match that of the target resolution
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

VisualUpload.propTypes = {
  onUpdate: PropTypes.func,
};

export default VisualUpload;
